import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import MyDropzone from 'components/MyDropzone/MyDropzone'
import { createBulkLocations, initializeMyCenters } from 'slices/locationSlice'
import {
	deleteAllLocations,
	deleteOneLocation,
	initializeBulkLocations,
	updateBulkLocation,
} from 'slices/bulkLocationSlice'
import notify from 'components/ui/notify'
import { initializeApiKey } from 'slices/apiKeysSlice'
import Spinner from 'components/Spinner'
import {
	XCircle,
	ArrowUpDown,
	Search,
	Sparkles,
	CheckCircle2,
	Circle,
	ChevronUp,
	ChevronDown,
} from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { TablePagination } from 'components/ui/pagination'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'

const sortFields = [
	{ id: 'assetName', label: 'Name' },
	{ id: 'retailer', label: 'Retailer' },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'postalCode', label: 'Zip' },
]

// Helper: enrich a single location with Google Places data
async function enrichSingleLocation(service, OK, location) {
	const query = `${location.assetName || ''} ${location.city || ''}, ${location.stateProvince || ''}`

	return new Promise(resolve => {
		service.textSearch({ query }, (results, status) => {
			if (status !== OK || !results || results.length === 0) {
				resolve(null)
				return
			}

			const placeId = results[0].place_id
			if (!placeId) {
				resolve(null)
				return
			}

			const enrichedData = {
				...location,
				googlePlaceId: placeId,
			}

			if (!location.retailer) {
				if (location.parentRetailer) {
					enrichedData.retailer = location.parentRetailer
				} else if (results[0].name) {
					enrichedData.retailer = results[0].name
				}
			}

			service.getDetails({ placeId, fields: ['photos'] }, async (detailResult, detailStatus) => {
				if (detailStatus === OK && detailResult?.photos && detailResult.photos.length > 0) {
					try {
						const photoUrl = detailResult.photos[0].getUrl({ maxWidth: 800 })
						const uploaded = await uploadToCloudinary(photoUrl)
						enrichedData.image = uploaded.secure_url
					} catch (err) {
						console.error('[Enrich] Photo upload failed:', err)
					}
				}
				resolve(enrichedData)
			})
		})
	})
}

const PortfolioBulkLoadLocationsPopup = ({ userId }) => {
	const dispatch = useDispatch()
	const placesRef = useRef(null)

	const [isEnriching, setIsEnriching] = useState(false)
	const [enrichProgress, setEnrichProgress] = useState({ current: 0, total: 0 })

	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const searchInputRef = useRef(null)
	const [searchInputValue, setSearchInputValue] = useState('')

	const { entity: apiKeyState } = useSelector(state => state.apiKey)
	const apiKey = apiKeyState?.key

	const { entities: locations, ids: locationIds, isLoading, isInitialized } = useSelector(
		state => state.bulkLocations,
	)
	const records = locationIds.map(id => locations[id])

	useEffect(() => {
		dispatch(initializeMyCenters())
		if (!isInitialized) {
			dispatch(initializeBulkLocations())
		}
		dispatch(initializeApiKey('REACT_APP_GOOGLE_API_KEY'))
	}, [dispatch, isInitialized])

	const hasRows = records && records.length > 0

	const filteredRecords = useMemo(() => {
		if (!records || records.length === 0) return []
		if (!searchInputValue) return records
		const lowerValue = searchInputValue.toLowerCase()
		return records.filter(
			x =>
				(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
				(x.retailer && x.retailer.toLowerCase().includes(lowerValue)) ||
				(x.address && x.address.toLowerCase().includes(lowerValue)) ||
				(x.city && x.city.toLowerCase().includes(lowerValue)) ||
				(x.stateProvince && x.stateProvince.toLowerCase().includes(lowerValue)) ||
				(x.postalCode && x.postalCode.toLowerCase().includes(lowerValue)),
		)
	}, [records, searchInputValue])

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a, b) => {
			const aVal = (a[orderBy] || '').toString().toLowerCase()
			const bVal = (b[orderBy] || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	const handleSort = fieldId => {
		if (orderBy === fieldId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(fieldId)
			setOrder('asc')
		}
	}

	const SortIcon = ({ fieldId }) => {
		if (orderBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	const handleSearch = e => {
		setSearchInputValue(e.target.value)
		setPage(0)
	}

	const handlePostBulkLocations = async () => {
		await dispatch(createBulkLocations({ locations: records, activeStatus: true }))
	}

	const handleClearList = () => {
		notify({
			title: `Clear List!`,
			text: `This will clear the current import list without posting the new locations.`,
			icon: 'success',
			buttons: {
				confirm: {
					text: 'Confirm',
					value: true,
					visible: true,
					className: '',
					closeModal: true,
				},
				cancel: {
					text: 'Cancel',
					value: null,
					visible: true,
					className: '',
					closeModal: true,
				},
			},
		}).then(async willDelete => {
			if (willDelete) {
				await dispatch(deleteAllLocations())
				dispatch(initializeBulkLocations())
				notify({
					title: `List Cleared!`,
					text: `The current list has been cleared.`,
					icon: 'success',
				})
			}
		})
	}

	const handleDeleteLocation = async locationId => {
		await dispatch(deleteOneLocation(locationId))
		dispatch(initializeBulkLocations())
	}

	// Google Places enrichment
	const enrichWithGooglePlaces = useCallback(async () => {
		if (!placesRef.current || isEnriching) return

		const toEnrich = records.filter(loc => !loc.googlePlaceId)
		if (toEnrich.length === 0) {
			notify({ title: 'All Done', text: 'All locations already have Google Place data.', icon: 'info' })
			return
		}

		setIsEnriching(true)
		setEnrichProgress({ current: 0, total: toEnrich.length })

		const service = new window.google.maps.places.PlacesService(placesRef.current)
		const OK = window.google.maps.places.PlacesServiceStatus.OK

		for (let i = 0; i < toEnrich.length; i++) {
			const loc = toEnrich[i]
			setEnrichProgress({ current: i + 1, total: toEnrich.length })

			try {
				const enriched = await enrichSingleLocation(service, OK, loc)
				if (enriched) {
					await dispatch(updateBulkLocation(enriched))
				}
			} catch (err) {
				console.error(`[Enrich] Failed for "${loc.assetName}":`, err)
			}

			if (i < toEnrich.length - 1) {
				await new Promise(resolve => setTimeout(resolve, 300))
			}
		}

		setIsEnriching(false)
		dispatch(initializeBulkLocations())
	}, [records, isEnriching, dispatch])

	return (
		<div>
			{/* Hidden div for Google Places service */}
			<div ref={placesRef} className="hidden" />

			{!hasRows ? (
				<MyDropzone apiKey={apiKey} />
			) : isLoading ? (
				<Spinner containerHeight="50vh" />
			) : (
				<>
					{/* Search + Enrich toolbar */}
					<div className="mb-3 flex items-center justify-between gap-4">
						<div className="relative flex-1 max-w-sm">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								ref={searchInputRef}
								type="text"
								placeholder="Search by name, retailer, address, city, state, or zip..."
								value={searchInputValue}
								onChange={handleSearch}
								className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
							/>
						</div>
						<button
							type="button"
							onClick={enrichWithGooglePlaces}
							disabled={isEnriching}
							className="inline-flex items-center gap-1.5 rounded-lg border border-[#0080fe] bg-white px-3 py-2 text-xs font-medium text-[#0080fe] hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
							title="Enrich with Google Places"
						>
							<Sparkles className="h-3 w-3" />
							{isEnriching
								? `Enriching ${enrichProgress.current}/${enrichProgress.total}...`
								: 'Enrich with Google'}
						</button>
					</div>

					{/* Sort pills */}
					<div className="mb-3 flex items-center gap-1.5 flex-wrap">
						<span className="text-xs text-gray-400 mr-1">Sort by:</span>
						{sortFields.map(field => (
							<button
								key={field.id}
								type="button"
								onClick={() => handleSort(field.id)}
								className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
									orderBy === field.id
										? 'bg-[#0080fe]/10 text-[#0080fe]'
										: 'bg-gray-100 text-gray-500 hover:bg-gray-200'
								}`}
							>
								{field.label}
								<SortIcon fieldId={field.id} />
							</button>
						))}
					</div>

					{/* Enrich progress bar */}
					{isEnriching && (
						<div className="mb-3">
							<div className="flex items-center gap-2 mb-1">
								<Sparkles className="h-3 w-3 text-[#0080fe] animate-pulse" />
								<span className="text-xs text-gray-500">
									Enriching {enrichProgress.current} of {enrichProgress.total} locations...
								</span>
							</div>
							<div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
								<div
									className="h-full bg-[#0080fe] rounded-full transition-all duration-300 ease-out"
									style={{
										width: `${enrichProgress.total > 0 ? (enrichProgress.current / enrichProgress.total) * 100 : 0}%`,
									}}
								/>
							</div>
						</div>
					)}

					{/* List items */}
					<div className="divide-y divide-gray-100">
						{paginatedRecords.map(location => (
							<div key={location._id} className="flex items-center gap-3 py-3 group">
								{/* Avatar */}
								<Avatar className="h-10 w-10 flex-shrink-0">
									<AvatarImage src={location.image} />
									<AvatarFallback className="text-sm bg-gray-100 text-gray-400">
										{location.assetName?.[0] || '?'}
									</AvatarFallback>
								</Avatar>

								{/* Details */}
								<div className="min-w-0 flex-1">
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium text-gray-900 truncate">
											{location.assetName}
										</span>
										{location.retailer && (
											<span className="text-xs text-gray-400 truncate">
												{location.retailer}
											</span>
										)}
										{/* GP status */}
										{location.googlePlaceId ? (
											<CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
										) : (
											<Circle className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
										)}
									</div>
									<div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
										{location.address && (
											<span className="truncate">{location.address},</span>
										)}
										<span className="truncate">
											{location.city}{location.stateProvince ? `, ${location.stateProvince}` : ''}
										</span>
										{location.postalCode && (
											<span className="flex-shrink-0">{location.postalCode}</span>
										)}
									</div>
								</div>

								{/* Delete */}
								<button
									type="button"
									onClick={() => handleDeleteLocation(location._id)}
									className="flex-shrink-0 p-1.5 rounded-full text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
									title="Delete"
								>
									<XCircle className="h-4 w-4" />
								</button>
							</div>
						))}
					</div>

					{sortedRecords.length > 4 && (
						<TablePagination
							page={page}
							rowsPerPage={rowsPerPage}
							count={sortedRecords.length}
							onPageChange={setPage}
							onRowsPerPageChange={setRowsPerPage}
						/>
					)}

					{/* Action Buttons */}
					<div className="flex items-center justify-center gap-4 mt-4">
						<button
							type="button"
							onClick={handleClearList}
							className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
						>
							Clear List
						</button>
						<button
							type="button"
							onClick={handlePostBulkLocations}
							className="inline-flex items-center gap-1.5 rounded-lg bg-[#0080fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#0070e0] transition-colors"
						>
							Post List
						</button>
					</div>
				</>
			)}
		</div>
	)
}

export default PortfolioBulkLoadLocationsPopup
